import { Colors } from "@blueprintjs/core";
import {
  createColDef,
  createColDefToggleSwitch,
  ItemGridFilterEnabled,
  ItemGridFilterFlyoutType,
  ItemGridFilterHideSearch,
  ItemGridFilterLayouts,
  ItemGridFilterTags,
  ItemGridFilterTileType,
} from "@package/ui-apollo-components";
import { useSaveToaster } from "@package/ui-components";
import {
  CreatePostDocument,
  DeletePostDocument,
  GetPostDocument,
  GetPostGridDocument,
  GetPostListNameIdDocument,
  ServiceTypes,
  useEnablePostMutation,
  useGetPostGridQuery,
  useGetServiceListNameIdQuery,
  useGetTagListNameIdQuery,
  type Post,
} from "@package/ui-gql/index-data-access";
import type {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from "ag-grid-community";
import { uniq } from "lodash";
import { Fragment, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  ITEM_GRID_PATH_EDIT,
  SidebarEntryPath,
} from "../../../../constants/contants";
import { useAppSelector } from "../../../../store/store";
import { ItemGridWithToolbar } from "../../../ItemGrid/ItemGridWithToolbar";

export function PostGrid() {
  const variables = useAppSelector((state) => state.itemGrid.posts.variables);
  const { search } = useLocation();
  const navigate = useNavigate();

  const {
    data: dataPosts,
    loading: loadingPosts,
    error: errorPosts,
    refetch: refetchPosts,
  } = useGetPostGridQuery({ variables });
  const {
    data: dataTags,
    loading: loadingTags,
    error: errorTags,
  } = useGetTagListNameIdQuery({
    variables: {
      page: 0,
      perPage: 1000,
    },
  });
  const {
    data: dataServicesFlyout,
    loading: loadingServicesFlyout,
    error: errorServicesFlyout,
  } = useGetServiceListNameIdQuery({
    variables: {
      filter: {
        type: ServiceTypes.Flyout,
      },
      page: 0,
      perPage: 1000,
    },
  });
  const [
    updatePostEnabled,
    { loading: loadingPostEnabled, error: errorPostEnabled },
  ] = useEnablePostMutation();

  useSaveToaster({
    title: "Content",
    inflight: loadingPostEnabled,
    success: !loadingPostEnabled && !errorPostEnabled,
  });

  const columnDefs = useMemo<ColDef[]>(
    () => [
      createColDef<Post>("title", "Title", 1, 200),
      createColDefToggleSwitch<Post>(
        "enabled",
        "Enabled",
        loadingPosts || loadingPostEnabled,
        (post, enabled) => {
          if (post.id) {
            updatePostEnabled({
              variables: {
                id: post.id,
                enabled,
              },
              onCompleted: () => refetchPosts(),
            });
          }
        },
      ),
      {
        ...createColDef<Post>("tags", "Tags", 1, 200),
        cellRenderer: (
          params: ICellRendererParams<Post, string[] | undefined>,
        ) => {
          if (Array.isArray(params.value)) {
            const tagIds = uniq(params.value);
            const anchors = tagIds
              .map((id) => dataTags?.items?.find((tag) => tag.id === id))
              .filter((tag) => !!tag)
              .map((tag, index) => (
                <a
                  key={`${tag.id}-${index}`}
                  style={{ color: Colors.BLUE1 }}
                  onClick={() =>
                    navigate(
                      `/${SidebarEntryPath.tags}/${ITEM_GRID_PATH_EDIT}/${tag.id}${search}`,
                    )
                  }
                >
                  {tag.title}
                </a>
              ));

            if (anchors.length) {
              return (
                <div className="flex flex-row w-full h-full">
                  <div className="grow">
                    {anchors.map((anchor, index) => {
                      return (
                        <Fragment key={index}>
                          {anchor}
                          {index !== anchors.length - 1 ? ", " : ""}
                        </Fragment>
                      );
                    })}
                  </div>
                </div>
              );
            }
          }

          return "";
        },
      },
      {
        ...createColDef<Post>("flyoutType", "Interactive Type", 1, 200),
        valueFormatter: (params: ValueFormatterParams<Post>) => {
          return (
            dataServicesFlyout?.items?.find(
              (service) => service.id === params.value,
            )?.name ?? ""
          );
        },
      },
      createColDef<Post>("updatedAt", "Last Updated", 1, 125),
    ],
    [
      dataServicesFlyout?.items,
      dataTags?.items,
      loadingPostEnabled,
      loadingPosts,
      navigate,
      refetchPosts,
      search,
      updatePostEnabled,
    ],
  );

  return (
    <ItemGridWithToolbar
      propsItemGrid={{
        sidebarEntryPath: SidebarEntryPath.posts,
        columnDefs,
        data: dataPosts,
        error: !!errorPosts || !!errorTags || !!errorServicesFlyout,
        loading:
          !dataPosts && (loadingPosts || loadingTags || loadingServicesFlyout),
        filtersSupported: true,
        filterPanels: [
          <ItemGridFilterEnabled key="enabled" />,
          <ItemGridFilterTags key="tags" />,
          <ItemGridFilterTileType key="tileType" />,
          <ItemGridFilterFlyoutType key="flyoutTypes" />,
          <ItemGridFilterHideSearch key="hideSearch" />,
          <ItemGridFilterLayouts key="layouts" />,
        ],
      }}
      propsItemGridToolbar={{
        title: "Content",
        sidebarEntryPath: SidebarEntryPath.posts,
        createEnabled: true,
        deleteDocument: DeletePostDocument,
        deleteEnabled: true,
        deleteRefetchQueries: [
          GetPostDocument,
          GetPostGridDocument,
          GetPostListNameIdDocument,
        ],
        duplicateCreateDocument: CreatePostDocument,
        duplicateEnabled: true,
        duplicateGetDocument: GetPostDocument,
        duplicateNameField: "title",
        duplicateRefetchQueries: [
          GetPostGridDocument,
          GetPostListNameIdDocument,
        ],
        editEnabled: true,
        exportEnabled: false,
      }}
    />
  );
}
